"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { Zap, ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "Separate permission",
    rule: "Submitting is guarded by its own Submit Production right, distinct from the right that lets you view or create production orders",
  },
  {
    topic: "BOM Based: partial submissions are real",
    rule: "You can submit less than the full planned quantity more than once. Each submission adds to a running total that can never exceed the order's quantity",
  },
  {
    topic: "Process Route: no partial concept",
    rule: "There's no BOM-style partial status. The order stays open until every process has been started and ended, in order",
  },
  {
    topic: "Sequential processes only",
    rule: "A process can't be ended before the one before it has already ended. You can't skip ahead or run steps out of order",
  },
  {
    topic: "Finished Goods Warehouse can't be changed here",
    rule: "Whatever was set on Add Production is where output lands. There is no warehouse picker on any submit screen",
  },
  {
    topic: "FG Rejected Qty is just a number",
    rule: "It's recorded and totalled, but nothing else happens to it, no stock movement, no separate rejection document",
  },
  {
    topic: "Completed can't be reopened",
    rule: "Once an order reaches Completed, submitting again is blocked outright",
  },
  {
    topic: "No Cancel action",
    rule: "The only status action beyond submitting is Mark as Close, and it's only available from Pending, Partial, Inventory Issued, Waiting for Inventory Approval, or Inventory Rejected. It never reverses stock or the ledger, it just stops the order",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Two different submit flows, one per method" },
      { heading: "2. Where stock actually moves" },
      { heading: "3. What it costs, and where that posts in your books" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function SubmitProductionPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Submit Production
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone recording what a production run actually produced
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            <span className="geist-medium text-[#111827]">Submit Production</span>{" "}
            is where an order created in Add Production turns into real
            numbers: finished goods actually made, raw materials actually
            used, and a cost per unit. What that screen looks like depends
            entirely on which method the order was created under. This is
            also the point where the run stops being a plan and starts
            genuinely affecting the rest of ABSS Books: real stock batches
            move in Inventory, and a real journal entry posts in Accounting.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Two flows */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Two different submit flows, one per method
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            BOM Based and Process Route orders aren&apos;t just different in
            setup, they're submitted through entirely separate screens.
          </p>
          <Definitions
            items={[
              {
                term: "BOM Based",
                desc: "One form, entered as many times as you need. Each submission enters an FG Submitted Qty and FG Rejected Qty, plus how much of each raw material was actually consumed.",
              },
              {
                term: "Running totals",
                desc: "Every submission adds to the order's total submitted and total rejected quantity. The order moves to Partial once you're short of the planned quantity, and to Completed once submitted plus rejected reaches it.",
              },
              {
                term: "Process Route",
                desc: "No single submit action. Each Process step is Started, then later Ended, one at a time and in order. Ending the last process is what completes the order.",
              },
              {
                term: "Ending a process asks for",
                desc: "The raw materials that step actually consumed, any manpower and hours worked, an electricity unit reading, any scrap produced, an additional cost with notes, and the step's own output quantity.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the BOM Based submit form with FG Submitted Qty, FG Rejected Qty, and per-material consumption fields." />
          <Screenshot caption="Screenshot: an End Process form for a Process Route step, showing raw material input, manpower, and scrap fields." />

          {/* 2. Where stock moves */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Where stock actually moves
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            This is the point where materials genuinely leave stock and
            finished goods genuinely enter it, batch by batch.
          </p>
          <Definitions
            items={[
              {
                term: "Raw materials",
                desc: "Deducted from the Production Warehouse, at real batch level, the moment a BOM Based submission or a Process Route step is entered.",
              },
              {
                term: "Finished goods",
                desc: "Added as a new batch to whichever Finished Goods Warehouse was set back on Add Production. That warehouse can't be changed at this stage.",
              },
              {
                term: "Scrap",
                desc: "If a process produces scrap, it's added to stock too, as its own item, its own batch, and its own cost, not just logged as a quantity.",
              },
              {
                term: "Manpower rate",
                desc: "Selecting a worker auto-fills their current hourly rate from ManPower. It's copied onto this submission at that moment, so a later change to the worker's rate never rewrites what's already been recorded here.",
              },
            ]}
          />
          <Callout type="note">
            FG Rejected Qty and a raw material&apos;s own reject quantity are
            two different things. Raw material rejects are still deducted
            from stock as consumed. FG Rejected Qty is only ever a number
            added to a running total, nothing else happens to it.
          </Callout>
          <Screenshot caption="Screenshot: the Stock Movement tab on a completed order, showing raw material batches deducted and finished-goods batches added." />

          {/* 3. Cost and ledger */}
          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. What it costs, and where that posts in your books
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Every submission recalculates a real cost per unit and posts a
            journal entry, not just a stock movement.
          </p>
          <Definitions
            items={[
              {
                term: "Cost per unit",
                desc: "Raw materials at their actual batch cost, plus labour, plus overhead, minus any scrap recovered, divided by the quantity produced. You can see this breakdown on the order's Cost Breakdown tab.",
              },
              {
                term: "The journal",
                desc: "Debits the finished item's inventory account for the total value produced, credits each raw material's own inventory account for what it consumed, and credits system Labour and Job Costing accounts for the labour and overhead portions.",
              },
              {
                term: "Rounding",
                desc: "A small leftover from rounding lands in a Round Off account. A larger mismatch is treated as a real production gain or loss and lands in Cost of Goods Sold instead.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Cost Breakdown tab showing raw materials, labour, overhead, scrap recovered, and cost per unit." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 flex-wrap">
          <Link
            href="/documentation/books/production/add-production"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Add Production <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where the order and its three warehouses are set up first
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/manpower"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              ManPower <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where a worker's hourly rate actually comes from
            </p>
          </Link>
          <Link
            href="/documentation/books/accountant/chart-of-accounts"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              General Ledger <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where a submission's journal entry actually posts
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
